import {
  IonContent,
  IonPage,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonText,
  IonSpinner,
} from '@ionic/react';
import { addCircleOutline } from 'ionicons/icons';
import { useQuery } from '@apollo/client/react';
import { GET_FANTASY_LEAGUE } from '../../../graphql/queries/fantasyLeague';
import { GET_ACTIVE_MARKET_ENTRIES } from '../../../graphql/queries/activeMarketEntries';
import { useUserAccount } from '../../../hooks/useUserAccount';
import { PlayerCard } from '../components/PlayerCard';
import type {
  GetFantasyLeagueQuery,
  GetFantasyLeagueQueryVariables,
  GetActiveMarketEntriesQuery,
  GetActiveMarketEntriesQueryVariables,
} from '../../../types/graphql-generated';
import { useParams } from 'react-router-dom';
import { useState } from 'react';
import { AddPlayerPriceModal } from '../components/AddPlayerPriceModal';
import { AppSectionHeader } from '../../components/ui/AppSectionHeader';

export const AddPlayerToMarket = () => {
  const leagueId = useParams<'leagueId'>().leagueId!;
  const { userAccount } = useUserAccount();
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data, loading, error } = useQuery<
    GetFantasyLeagueQuery,
    GetFantasyLeagueQueryVariables
  >(GET_FANTASY_LEAGUE, {
    variables: { id: leagueId },
  });

  const { data: marketData, loading: marketLoading, error: marketError } = useQuery<
    GetActiveMarketEntriesQuery,
    GetActiveMarketEntriesQueryVariables
  >(GET_ACTIVE_MARKET_ENTRIES, {
    variables: { fantasyLeagueId: leagueId },
  });

  const league = data?.fantasyLeague;
  const marketEntries = marketData?.activeMarketEntries || [];

  // Find the current user's member record
  const currentMember = league?.members.find(
    (member) => member.user.id === userAccount?.id
  );

  // Extract IDs of players already in the market
  const listedPlayerIds = new Set(
    marketEntries.map(entry => entry.player.id)
  );

  // Filter out already-listed players
  const availablePlayers = currentMember?.fantasyTeamPlayers.filter(
    ftp => !listedPlayerIds.has(ftp.player.id)
  ) || [];

  const selectedPlayer = availablePlayers
    .map(ftp => ftp.player)
    .find(player => player.id === selectedPlayerId);

  const handlePlayerClick = (playerId: string) => {
    setSelectedPlayerId(playerId);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedPlayerId(null);
  };

  // Group players by role
  const groupPlayersByRole = () => {
    if (!availablePlayers || availablePlayers.length === 0) return {};

    const players = availablePlayers.map(ftp => ftp.player);
    return players.reduce((acc: Record<string, typeof players>, player) => {
      const role = player.role || 'Sin posición';
      if (!acc[role]) {
        acc[role] = [];
      }
      acc[role].push(player);
      return acc;
    }, {});
  };

  const playersByRole = groupPlayersByRole();

  // Role order for display (using GraphQL enum values)
  const roleOrder = ['PORTERO', 'DEFENSA', 'CENTROCAMPISTA', 'DELANTERO', 'Sin posición'];

  return (
    <IonPage>
      <AppSectionHeader variant="fantasy">
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton />
          </IonButtons>
          <IonTitle className="ml-4">Poner jugador en venta</IonTitle>
        </IonToolbar>
      </AppSectionHeader>
      <IonContent className="ion-padding">
        {(loading || marketLoading) && (
          <div className="flex justify-center items-center min-h-[200px]">
            <IonSpinner name="crescent" />
          </div>
        )}

        {(error || marketError) && (
          <IonText color="danger">
            <p>Error al cargar tus jugadores</p>
          </IonText>
        )}

        {!loading && !currentMember && (
          <IonText>
            <p>No eres miembro de esta liga</p>
          </IonText>
        )}

        {currentMember && currentMember.fantasyTeamPlayers.length === 0 && (
          <IonText>
            <div className="text-center mt-8">
              <h2 className="text-xl font-semibold">No tienes jugadores</h2>
              <p className="text-gray-500 mt-2">
                Aún no tienes jugadores en tu equipo
              </p>
            </div>
          </IonText>
        )}

        {currentMember && currentMember.fantasyTeamPlayers.length > 0 && availablePlayers.length === 0 && (
          <IonText>
            <div className="text-center mt-8">
              <h2 className="text-xl font-semibold">Todos tus jugadores están ya en venta</h2>
              <p className="text-gray-500 mt-2">
                No tienes jugadores disponibles para vender
              </p>
            </div>
          </IonText>
        )}

        {currentMember && availablePlayers.length > 0 && (
          <div className="space-y-4">
            <div className="px-2">
              <h2 className="text-lg font-semibold">Tus jugadores</h2>
              <p className="text-sm text-gray-500">
                Selecciona un jugador para ponerlo en venta
              </p>
            </div>

            {roleOrder.map((role) => {
              const players = playersByRole[role];
              if (!players || players.length === 0) return null;

              return (
                <div key={role}>
                  <div className="space-y-2">
                    {players.map((player) => (
                      <PlayerCard
                        key={player.id}
                        player={player}
                        footerActions={[
                          {
                            label: 'Vender',
                            icon: addCircleOutline,
                            onClick: () => handlePlayerClick(player.id),
                          },
                        ]}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {selectedPlayer && (
          <AddPlayerPriceModal
            isOpen={isModalOpen}
            player={selectedPlayer}
            leagueId={leagueId}
            source="market_tab"
            onClose={handleModalClose}
          />
        )}
      </IonContent>
    </IonPage>
  );
};
